/**
 * Database Module Entry Point
 * Initializes and exports the database service and related utilities
 */

// Import all services
import * as services from './services/index.js'

import { COLLECTIONS, PERMISSIONS, REQUIRED_INDEXES, ROLES, ROLE_PERMISSIONS, SCHEMAS, getDatabaseConfig } from './config.js'
import { QUERY_OPERATORS, SORT_DIRECTION } from './interfaces/IDatabaseAdapter.js'
import databaseService, { DATABASE_TYPES } from './DatabaseService.js'

/**
 * Initialize the database service
 * This should be called early in the application lifecycle
 */
export async function initializeDatabase() {
  try {
    const config = getDatabaseConfig()
    
    await databaseService.initialize(config.type, config.config)
    
    // Set the database service on all services
    setDatabaseServiceOnAllServices(databaseService)
    
    // Ensure required indexes exist (but don't fail if it doesn't work)
    try {
      await ensureRequiredIndexes(databaseService)
    } catch (indexError) {
      console.warn('⚠️ Could not ensure indexes (non-critical):', indexError.message)
    }
    
    return databaseService
  } catch (error) {
    console.error('❌ Failed to initialize database:', error)
    
    // Always try HttpAdapter as fallback in development
    console.warn('🚀 Trying HttpAdapter fallback...')
    
    try {
      // Try to initialize with HttpAdapter instead
      const rawApiBaseUrl = import.meta.env.VITE_API_BASE_URL
      const configuredApiBaseUrl = rawApiBaseUrl === undefined ? undefined : rawApiBaseUrl.trim()
      await databaseService.initialize(DATABASE_TYPES.HTTP, {
        baseUrl: configuredApiBaseUrl ?? 'https://staging.services.leadconnectorhq.com/hltestmanager',
        timeout: 10000
      })
      
      // Set the database service on all services
      setDatabaseServiceOnAllServices(databaseService)
      
      return databaseService
    } catch (httpError) {
      console.warn('⚠️ HttpAdapter also failed:', httpError.message)
      console.warn('🚀 Continuing with limited functionality...')
      // Don't throw the error, let the app continue
      return null
    }
  }
}

/**
 * Set the database service on all service instances
 * @param {Object} dbService - Database service instance
 */
function setDatabaseServiceOnAllServices(dbService: any): void {
  
  // Import all services and set their database service
  Object.entries(services).forEach(([serviceName, service]) => {
    // Skip the 'default' export which is just an object containing all services
    if (serviceName === 'default') return
    
    if (service && typeof (service as any).setDatabaseService === 'function') {
      (service as any).setDatabaseService(dbService)
    } else {
      console.warn(`⚠️ Service ${serviceName} does not have setDatabaseService method:`, service)
    }
  })
  
}

/**
 * Ensure all required indexes exist for optimal performance
 */
async function ensureRequiredIndexes(dbService: any): Promise<void> {
  try {
    for (const [collection, indexes] of Object.entries(REQUIRED_INDEXES)) {
      await dbService.ensureIndexes(collection, indexes)
    }
  } catch (error) {
    console.error('Error ensuring indexes:', error)
    // Don't throw here as index creation might not be critical for app startup
  }
}

/**
 * Validate document data against schema
 * @param {string} collection - Collection name
 * @param {Object} data - Document data
 * @returns {Object} Validation result
 */
export function validateDocument(collection: string, data: any): { valid: boolean; errors: string[] } {
  const schema = SCHEMAS[collection]
  if (!schema) {
    return { valid: true, errors: [] }
  }

  const errors = []

  for (const [field, rules] of Object.entries(schema)) {
    const value = data[field]
    const rule = rules as any

    // Check required fields
    if (rule.required && (value === undefined || value === null)) {
      errors.push(`Field '${field}' is required`)
      continue
    }

    // Skip validation if field is not present and not required
    if (value === undefined || value === null) {
      continue
    }

    // Type validation
    if (rule.type) {
      const isValid = validateFieldType(value, rule.type)
      if (!isValid) {
        errors.push(`Field '${field}' must be of type '${rule.type}'`)
      }
    }

    // Enum validation
    if (rule.enum && !rule.enum.includes(value)) {
      errors.push(`Field '${field}' must be one of: ${rule.enum.join(', ')}`)
    }
  }

  return {
    valid: errors.length === 0,
    errors
  }
}

/**
 * Validate field type
 * @param {any} value - Field value
 * @param {string} type - Expected type
 * @returns {boolean} Whether the value matches the expected type
 */
function validateFieldType(value: any, type: string): boolean {
  switch (type) {
    case 'string':
      return typeof value === 'string'
    case 'number':
      return typeof value === 'number'
    case 'boolean':
      return typeof value === 'boolean'
    case 'array':
      return Array.isArray(value)
    case 'object':
      return typeof value === 'object' && !Array.isArray(value)
    case 'datetime':
      return value instanceof Date || typeof value === 'string' || typeof value === 'number'
    default:
      return true
  }
}

/**
 * Create standardized query options
 * @param {Object} options - Query options
 * @returns {Object} Normalized query options
 */
export function createQueryOptions(options: any = {}): any {
  return {
    where: options.where || [],
    orderBy: options.orderBy || [],
    limit: options.limit || null,
    offset: options.offset || null,
    startAfter: options.startAfter || null
  }
}

/**
 * Create a where condition
 * @param {string} field - Field name
 * @param {string} operator - Query operator
 * @param {any} value - Comparison value
 * @returns {Object} Where condition
 */
export function where(field: string, operator: string, value: any): { field: string; operator: string; value: any } {
  return { field, operator, value }
}

/**
 * Create an order by condition
 * @param {string} field - Field name
 * @param {string} direction - Sort direction ('asc' or 'desc')
 * @returns {Object} Order by condition
 */
export function orderBy(field: string, direction: string = SORT_DIRECTION.ASC): { field: string; direction: string } {
  return { field, direction }
}

// Export the database service instance and utilities
export { 
  databaseService as db,
  COLLECTIONS,
  SCHEMAS,
  REQUIRED_INDEXES,
  ROLES,
  PERMISSIONS,
  ROLE_PERMISSIONS,
  DATABASE_TYPES,
  QUERY_OPERATORS,
  SORT_DIRECTION
}

// Export all services
export * from './services/index.js'

// Export default as the database service
export default databaseService
